import cn from 'classnames';
import React, {
  FC,
  Fragment,
  MouseEvent,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PopoverVirtualElement, Typography } from '@mui/material';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import logo from '@assets/logos/logo.png';

import Button from '@components/common/Button';
import ChainButton from '@components/common/ChainButton';
import ConnectWalletButton from '@components/common/ConnectWalletButton';
import Container from '@components/common/Container';
import SubmenuRenderer from '@components/common/Layout/Header/HeaderContainer/SubmenuRenderer';

import { IDs } from '@constants/common/ids.constants';
import {
  AVAILABLE_HEADER_SUB_ROUTES,
  HEADER_ROUTES,
} from '@constants/common/menu.constants';
import { MODAL_TYPES } from '@constants/common/modals.constants';
import {
  PATH_DEX_SWAP,
  PATH_INDEX,
  PATH_VAULTS,
} from '@constants/common/routes.constants';
import { Size } from '@constants/common/ui.constants';

import useStyles from '../styles.module';
import HiddenLinks from './HiddenLinks';

interface Props {
  onCloseDrawer?: () => void;
  mobileView: boolean;
}

const HeaderContainer: FC<Props> = ({
  onCloseDrawer = undefined,
  mobileView,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { classes } = useStyles();
  const { account } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleNavigation = useCallback(
    (href: string, newTab?: boolean) => {
      if (newTab) {
        window.open(href, '_self');
      } else {
        navigate(href);
        onCloseDrawer?.();
      }
    },
    [navigate, onCloseDrawer],
  );

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCloseRedirect = useCallback(() => {
    setAnchorEl(null);
    navigate(PATH_VAULTS);
  }, [navigate]);

  const renderNavButtons = useMemo(
    () =>
      HEADER_ROUTES.map(({ title, href, submenu, newTab }) => {
        const isActive =
          href === pathname ||
          (href === PATH_DEX_SWAP &&
            AVAILABLE_HEADER_SUB_ROUTES.includes(pathname));
        const isOpen = Boolean(anchorEl);

        return (
          <Fragment key={title}>
            <div
              role="button"
              tabIndex={0}
              aria-owns={isOpen && submenu ? 'mouse-over-popover' : undefined}
              aria-haspopup="true"
              onClick={() =>
                submenu ? undefined : handleNavigation(href, newTab)
              }
              className={cn(classes.navLink, {
                [classes.activeNavLink]: submenu
                  ? isActive || isOpen
                  : isActive,
                [classes.desktop]: !mobileView,
              })}
              onMouseEnter={submenu ? handleClick : undefined}
              onMouseLeave={submenu ? handleClose : undefined}
              onKeyDown={() =>
                submenu ? undefined : handleNavigation(href, newTab)
              }
            >
              <button
                onClick={submenu ? handleCloseRedirect : undefined}
                className={cn(classes.navLink, {
                  [classes.activeNavLink]: submenu
                    ? isActive || isOpen
                    : isActive,
                  [classes.desktop]: !mobileView,
                })}
              >
                <Typography variant="subtitle1">{title}</Typography>
                {submenu &&
                  !mobileView &&
                  (anchorEl ? (
                    <KeyboardArrowUpIcon className={classes.arrow} />
                  ) : (
                    <KeyboardArrowDownIcon className={classes.arrow} />
                  ))}
              </button>
              {!mobileView && submenu && (
                <SubmenuRenderer
                  submenu={submenu}
                  handleClose={handleClose}
                  anchorEl={anchorEl}
                />
              )}
            </div>
          </Fragment>
        );
      }),
    [
      pathname,
      anchorEl,
      handleClick,
      handleClose,
      mobileView,
      onCloseDrawer,
      handleNavigation,
      handleCloseRedirect,
    ],
  );

  const handleChain = useCallback(() => {
    if (onCloseDrawer) {
      onCloseDrawer();
    }
    modalActions.show(MODAL_TYPES.CHAIN_MODAL);
  }, [onCloseDrawer]);

  const handleUserWallet = useCallback(() => {
    modalActions.show(MODAL_TYPES.ACCOUNT_MODAL);
  }, []);

  return (
    <div className={classes.header}>
      <Container className={cn(classes.row, classes.sideBar)}>
        <div className={classes.inner}>
          {!onCloseDrawer && (
            <Link to={PATH_INDEX} className={classes.link}>
              <img src={logo} className={classes.logo} alt="logo" />
            </Link>
          )}
          <nav
            className={cn(classes.nav, { [classes.navMob]: !!onCloseDrawer })}
          >
            {renderNavButtons}
            {/* <HiddenLinks mobileView={mobileView} onClose={onCloseDrawer} /> */}
          </nav>
        </div>
        <div className={classes.buttonWrap}>
          <ChainButton
            className={classes.chainButton}
            onClick={handleChain}
            fullWidth={!!onCloseDrawer}
            size={Size.Large}
          />
          {account ? (
            <Button
              onClick={handleUserWallet}
              className={classes.myWallet}
              id={IDs.HEADER_OPEN_WALLET_MODAL_BUTTON}
            >
              My wallet
            </Button>
          ) : (
            <ConnectWalletButton
              onClose={onCloseDrawer}
              fullWidth={mobileView}
              className={classes.connectWallet}
              id={IDs.HEADER_CONNECT_WALLET_BUTTON}
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default memo(HeaderContainer);
